import React, {useMemo} from 'react';
import List from '@mui/material/List';
import {useSelector} from 'react-redux';

import NavVerticalGroup from './VerticalNavGroup';
import VerticalCollapse from './VerticalCollapse';
import VerticalItem from './VerticalItem';

import {useAbility} from '@crema/utility/hooks/useAbility';
import {filterNavByAccess} from '@crema/utility/hooks/filterNavByAccess';
import {checkPermission} from '@crema/utility/helper/RouteHelper';

import routesConfig from '../../../../../pages/routesConfig';
import {useDynamicRoutes} from '../../../../../contexts/DynamicRoutesContext';
import {usePermissions} from '../../../../../hooks/usePermissions';

const VerticalNav = () => {
  const ability = useAbility();
  const {user} = useSelector(({user}) => user) || {};
  const {routes} = useDynamicRoutes();
  const {role: whatsappRole, serviceType} = usePermissions();

  // Portal user: build a Set of page_keys where can_view is true
  const portalViewableKeys = useMemo(() => {
    if (user?.role !== 'portal_user') return null;
    const perms = user?.portalPermissions || [];
    return new Set(perms.filter((p) => p.can_view).map((p) => p.page_key));
  }, [user?.role, user?.portalPermissions]);

  // Use dynamic routes if available, fallback to static config
  const currentRoutes = routes.length > 0 ? routes : routesConfig;

  // 1) Feature/permission filter
  const abilityFiltered = useMemo(
    () => filterNavByAccess(currentRoutes, ability),
    [currentRoutes, ability],
  );

  // 2) Role gate (permittedRole property) + service type filtering
  const finalFiltered = useMemo(() => {
    const applyRoleGate = (nodes) =>
      (Array.isArray(nodes) ? nodes : [])
        .map((n) => {
          // excludeRole: hide this item if current role matches any in excludeRole list
          if (n?.excludeRole && n.excludeRole.length > 0) {
            const excluded =
              n.excludeRole.includes(user?.role) ||
              n.excludeRole.includes(whatsappRole);
            if (excluded) return null;
          }

          // Check permittedRole first - restricts sections to specific roles only
          const hasPermittedRole =
            n?.permittedRole && n.permittedRole.length > 0;
          if (hasPermittedRole) {
            const roleAllowed =
              checkPermission(n.permittedRole, user?.role) ||
              checkPermission(n.permittedRole, whatsappRole);
            if (!roleAllowed) return null;
          }

          // Superadmin bypasses remaining auth checks (but NOT permittedRole above)
          if (whatsappRole === 'superadmin') {
            // Still check service type for messaging-only features
            if (
              n?.requiredServiceType &&
              n.requiredServiceType !== serviceType
            ) {
              return null;
            }

            // Process children recursively
            if (Array.isArray(n.children) && n.children.length) {
              const kids = applyRoleGate(n.children);
              return {...n, children: kids};
            }
            return n;
          }

          // Check auth (role-based permission) - CMS role OR WhatsApp role
          const hasAuth = n?.auth && n.auth.length > 0;
          const roleOK =
            !hasAuth ||
            checkPermission(n.auth, user?.role) ||
            checkPermission(n.auth, whatsappRole);
          if (!roleOK) return null;

          // Portal user: only show items where they have can_view permission
          if (portalViewableKeys !== null && n?.portalPageKey) {
            if (!portalViewableKeys.has(n.portalPageKey)) return null;
          }

          // Check requiredServiceType
          if (n?.requiredServiceType && n.requiredServiceType !== serviceType) {
            return null;
          }

          // Process children recursively
          if (Array.isArray(n.children) && n.children.length) {
            const kids = applyRoleGate(n.children);
            if (
              (n.type === 'group' || n.type === 'collapse') &&
              kids.length === 0
            )
              return null;
            return {...n, children: kids};
          }
          return n;
        })
        .filter(Boolean);

    return applyRoleGate(abilityFiltered);
  }, [
    abilityFiltered,
    user?.role,
    whatsappRole,
    serviceType,
    portalViewableKeys,
  ]);

  if (!finalFiltered.length) return null;

  return (
    <List sx={{position: 'relative', padding: 0}} component='div'>
      {finalFiltered.map((item) => (
        <React.Fragment key={item.id}>
          {item.type === 'group' && <NavVerticalGroup item={item} level={0} />}
          {item.type === 'collapse' && (
            <VerticalCollapse item={item} level={0} />
          )}
          {item.type === 'item' && <VerticalItem item={item} level={0} />}
        </React.Fragment>
      ))}
    </List>
  );
};

export default VerticalNav;
